import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoMdPrint } from "react-icons/io";
import { IoArrowBack } from "react-icons/io5";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import EventIcon from "@mui/icons-material/Event";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import dayjs from "dayjs";
import defaultUser from "../../../images/user_default.jpg";
import { encodedImagesURI } from "../../../utils/helpers";
import {
  PageHeader,
  SectionCard,
  StatCard,
  StatusChip,
  EmptyState,
  initials,
} from "../../../ui";
import { memberFeeChip } from "../../../utils/memberFeeStatus";

function formatDate(value) {
  if (!value) return "—";
  const d = dayjs(value);
  return d.isValid() ? d.format("MMM DD, YYYY") : "—";
}

function Field({ label, value }) {
  return (
    <div className="flex gap-3 py-3 border-b border-gx-border last:border-0">
      <div className="min-w-0 flex-1">
        <p className="text-[11px] uppercase tracking-wide font-semibold text-gx-ink-soft">
          {label}
        </p>
        <p className="text-sm font-medium text-gx-ink mt-0.5 break-words">
          {value || "—"}
        </p>
      </div>
    </div>
  );
}

function PrintRow({ label, value, allowEmpty = false }) {
  if (!allowEmpty && (value === undefined || value === null || value === "")) return null;
  return (
    <div className="flex border-b border-gx-border text-sm">
      <div className="w-1/3 p-2 font-semibold bg-gx-canvas">{label}</div>
      <div className="w-2/3 p-2 mx-5">{value || " "}</div>
    </div>
  );
}

const ProfileMember = () => {
  const location = useLocation();
  const member = location.state?.id;
  const prevPath = location.state?.prevPath;
  const navigate = useNavigate();
  const [role, setRole] = useState("");

  useEffect(() => {
    const sessionStr = sessionStorage.getItem("user-info");
    if (sessionStr) {
      const session = JSON.parse(sessionStr);
      setRole(session.user.role);
    } else {
      navigate("/login");
    }
  }, [navigate]);

  const isVisitor = prevPath === "/visitor";

  const handlePrint = () => window.print();

  const handleNavigate = () => {
    if (prevPath === "/visitor") {
      navigate("/visitor");
    } else if (prevPath === "/memberattendance") {
      navigate("/memberattendance");
    } else {
      navigate("/members");
    }
  };

  const subscription = member?.subscription;
  const startDate = subscription?.renewed_date || subscription?.start_date;
  const endDate = subscription?.end_date;
  const daysLeft = useMemo(() => {
    if (!endDate) return null;
    const end = dayjs(endDate);
    if (!end.isValid()) return null;
    return Math.max(end.diff(dayjs(), "day"), 0);
  }, [endDate]);

  const feeChip = memberFeeChip(member);

  const memberCode = member
    ? `${member?.location_id?.member_id_temp || ""}${member?.member_id || ""}` || "—"
    : "—";

  const photoSrc = encodedImagesURI(member?.photo) || defaultUser;

  if (!member) {
    return (
      <div className="m-5">
        <PageHeader
          title="Member dossier"
          actions={
            <button type="button" className="gx-cta-secondary" onClick={() => navigate("/members")}>
              Back to members
            </button>
          }
        />
        <EmptyState
          title="No member selected"
          description="Open a member from the roster to see their full profile."
          action={
            <button type="button" className="gx-cta" onClick={() => navigate("/members")}>
              Go to roster
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="bg-gx-canvas text-gx-ink print:bg-white print:text-black">
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 1cm;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            box-sizing: border-box;
          }
          .print-screen { display: none !important; }
          .print-only { display: block !important; }
          .bg-white, .print-sheet {
            background-color: white !important;
            margin: 0 auto !important;
            padding: 0 !important;
            width: 100% !important;
            max-width: 21cm;
            box-sizing: border-box;
          }
          .print-container {
            width: 100%;
            max-width: 800px;
            margin: 0 auto !important;
            padding: 0 !important;
          }
          .border-gx-border { border: 1px solid #d1d5db !important; }
          .rounded { border-radius: 0.375rem !important; }
          .bg-gx-canvas { background-color: #f9fafb !important; }
          .print\\:hidden { display: none !important; }
          .text-black { color: black !important; }
          .underline { text-decoration: underline !important; }
          .print\\:font-serif { font-family: "Times New Roman", serif !important; }
          img { max-height: 200px !important; object-fit: cover; }
          .text-center { text-align: center !important; }
          .border-gx-border.rounded {
            break-inside: avoid !important;
            margin-bottom: 0.5rem !important;
          }
        }
      `}</style>

      <div className="print-screen p-4 md:p-6 space-y-6">
        <PageHeader
          title={isVisitor ? "Visitor dossier" : "Member dossier"}
          subtitle={role ? `Opened as ${role}` : "Full profile, membership, and health record"}
          actions={
            <>
              <button type="button" className="gx-cta-secondary gap-2" onClick={handleNavigate}>
                <IoArrowBack />
                Back
              </button>
              <button type="button" className="gx-cta gap-2" onClick={handlePrint}>
                <IoMdPrint />
                Print sheet
              </button>
            </>
          }
        />

        <div className="gx-glass rounded-gx p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 border-l-[3px] border-l-gx-red">
          <div className="w-28 h-28 rounded-gx overflow-hidden border border-gx-border bg-gx-canvas shrink-0 shadow-gx">
            <img
              src={photoSrc}
              alt={member.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = defaultUser;
              }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-gx-ink tracking-tight truncate">
                {member.name || "Unnamed"}
              </h1>
              <StatusChip active={member.is_active !== false} />
              {!isVisitor ? (
                <StatusChip tone={feeChip.tone} label={feeChip.label} />
              ) : null}
              {member.is_new ? <StatusChip tone="navy" label="New" /> : null}
              {member.is_trainer ? <StatusChip tone="brand" label="Personal coaching" /> : null}
            </div>
            <p className="text-sm text-gx-ink-soft">
              {isVisitor ? "Visitor" : "Member"} code{" "}
              <span className="font-semibold text-gx-ink">{memberCode}</span>
              {member.location_id?.name ? ` · ${member.location_id.name}` : ""}
              {member.gender ? ` · ${member.gender}` : ""}
            </p>
            {member.package_id?.title ? (
              <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-gx-navy bg-gx-canvas border border-gx-border px-3 py-1 rounded-full">
                <CardMembershipIcon sx={{ fontSize: 16 }} />
                {member.package_id.title}
                {member.package_id.duration ? ` · ${member.package_id.duration}` : ""}
              </p>
            ) : null}
          </div>
          <div className="hidden sm:flex w-16 h-16 rounded-gx bg-gx-red/10 text-gx-red items-center justify-center text-xl font-bold shrink-0">
            {initials(member.name)}
          </div>
        </div>

        {!isVisitor ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <StatCard
              featured
              label="Package"
              value={member.package_id?.title || "None"}
              icon={<CardMembershipIcon />}
              hint={member.package_id?.duration || "No duration on file"}
            />
            <StatCard
              label="Subscription start"
              value={formatDate(startDate)}
              icon={<EventIcon />}
              accent="#033b51"
              hint={`Registered ${formatDate(member.created_at)}`}
            />
            <StatCard
              label="Subscription end"
              value={formatDate(endDate)}
              icon={<EventIcon />}
              accent="#F7931E"
              hint={daysLeft == null ? "No end date" : `${daysLeft} day${daysLeft === 1 ? "" : "s"} remaining`}
            />
            <StatCard
              label="Trainer"
              value={member.trainer_id?.name || "Unassigned"}
              icon={<FitnessCenterIcon />}
              accent="#D71920"
              hint={member.is_trainer ? "Personal coaching on" : "No coaching flag"}
            />
          </div>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SectionCard title="Personal details" icon={<PersonOutlineIcon />}>
            <Field label="Full name" value={member.name} />
            <Field label="Gender" value={member.gender} />
            <Field label="Date of birth" value={formatDate(member.date_of_birth)} />
            <Field label="Age" value={member.age} />
            <Field label="CNIC" value={member.cnic} />
            <Field label="Club" value={member.location_id?.name} />
          </SectionCard>

          <SectionCard title="Contact" icon={<PhoneOutlinedIcon />}>
            <Field label="Phone" value={member.phone} />
            <Field label="Email" value={member.email} />
            <Field label="Address" value={member.address} />
            <Field label="Postal code" value={member.postal_code} />
          </SectionCard>

          <SectionCard title="Emergency & health" icon={<LocalHospitalOutlinedIcon />}>
            {!isVisitor ? (
              <>
                <Field label="Emergency contact" value={member.emergency_contact} />
                <Field label="Emergency number" value={member.emergency_contact_no} />
              </>
            ) : null}
            <Field label="Disease (if any)" value={member.disease} />
            <Field label="Weight" value={member.weight} />
            <Field label="Height" value={member.height} />
          </SectionCard>

          {!isVisitor ? (
            <SectionCard title="Membership" icon={<BadgeOutlinedIcon />}>
              <Field label="Package" value={member.package_id?.title} />
              <Field
                label="Package ID"
                value={
                  member.package_id
                    ? `${member.package_id?.package_id_temp ?? ""}${member.member_id ?? ""}`
                    : null
                }
              />
              <Field label="Personal trainer" value={member.trainer_id?.name} />
              <Field label="Discount" value={member.discount} />
              <Field label="Member since" value={formatDate(member.created_at)} />
            </SectionCard>
          ) : (
            <SectionCard title="Visit" icon={<PlaceOutlinedIcon />}>
              <Field label="Club" value={member.location_id?.name} />
              <Field label="Recorded" value={formatDate(member.created_at)} />
            </SectionCard>
          )}
        </div>

        <div className="flex flex-wrap gap-2 print:hidden">
          {member.email ? (
            <a href={`mailto:${member.email}`} className="gx-cta-secondary gap-2">
              <EmailOutlinedIcon fontSize="small" />
              Email
            </a>
          ) : null}
          {member.phone ? (
            <a href={`tel:${member.phone}`} className="gx-cta-secondary gap-2">
              <PhoneOutlinedIcon fontSize="small" />
              Call
            </a>
          ) : null}
          {member.address ? (
            <span className="gx-cta-secondary gap-2 pointer-events-none">
              <HomeOutlinedIcon fontSize="small" />
              {member.location_id?.name || "On file"}
            </span>
          ) : null}
        </div>
      </div>

      <div className="hidden print-only print:block bg-white text-black px-8 py-6 print:px-0 print:py-0 print:font-serif">
        <div className="print-sheet landscape-container lg:mx-20 lg:px-[12rem] print:px-5 print:mx-5">
          <h1 className="text-3xl font-bold text-center mb-6 underline">
            {isVisitor ? "Visitor Information Sheet" : "Member Information Sheet"}
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8 items-start">
            <div className="flex justify-center">
              <img
                src={photoSrc}
                alt="Profile"
                className="rounded border w-64 h-64 object-cover"
              />
            </div>
            <div className="col-span-2 border border-gx-border rounded overflow-hidden">
              <PrintRow label="Full Name" value={member.name} />
              <PrintRow label="Gender" value={member.gender} />
              <PrintRow label="Date of Birth" value={formatDate(member.date_of_birth)} />
              <PrintRow label="Age" value={member.age} />
              <PrintRow label="Phone Number" value={member.phone} />
              <PrintRow label="Email Address" value={member.email} />
              <PrintRow label="CNIC" value={member.cnic} />
              <PrintRow label="Address" value={member.address} />
              <PrintRow label="Location" value={member.location_id?.name} />
            </div>
          </div>

          <h2 className="text-xl font-semibold mb-2">Emergency & Health Information</h2>
          <div className="border border-gx-border rounded mb-8">
            {!isVisitor && (
              <>
                <PrintRow label="Emergency Contact" value={member.emergency_contact} />
                <PrintRow label="Emergency Contact Number" value={member.emergency_contact_no} />
              </>
            )}
            <PrintRow label="Disease (if any)" value={member.disease} />
            <PrintRow label="Weight" value={member.weight} />
            <PrintRow label="Height" value={member.height} />
          </div>

          {!isVisitor && (
            <>
              <h2 className="text-xl font-semibold mb-2">Membership Details</h2>
              <div className="border border-gx-border rounded mb-8">
                <PrintRow label="Package" value={member.package_id?.title} />
                {member.package_id ? (
                  <PrintRow
                    label="Package ID"
                    value={`${member.package_id?.package_id_temp ?? ""}${member.member_id ?? ""}` || "—"}
                  />
                ) : null}
                <PrintRow label="Personal Trainer" value={member.trainer_id?.name} />
                <PrintRow label="Discount" value={member.discount} />
              </div>
            </>
          )}

          <h2 className="text-xl font-semibold mb-2">Contact Details</h2>
          <div className="border border-gx-border rounded mb-8">
            <PrintRow label="Primary Contact Phone" value={member.phone} />
            <PrintRow label="Email Address" value={member.email} />
          </div>

          <div className="mt-10 text-sm leading-relaxed">
            <h2 className="text-xl font-semibold mb-2">Additional Notes</h2>
            <div className="border border-gx-border rounded mb-8">
              <PrintRow label="Comments / Special Instructions" value=" " allowEmpty />
              <PrintRow label="Preferred Payment Method" value=" " allowEmpty />
              <PrintRow label="Preferred Billing Method" value=" " allowEmpty />
              <PrintRow label="Contract Name/No." value=" " allowEmpty />
              <PrintRow label="Payment Terms" value=" " allowEmpty />
            </div>
          </div>

          <div className="mt-12 text-sm leading-relaxed">
            <h2 className="text-lg font-semibold mb-2 underline">Disclaimer & Consent</h2>
            <p>☐ I confirm that the information provided is accurate to the best of my knowledge.</p>
            <p>☐ I understand that I must inform the gym staff of any change in health condition.</p>
            <p>☐ I acknowledge that I am participating in physical activities at my own risk.</p>
            <p>☐ I release Head On Gym and its staff from any liability for injuries or health complications arising during my participation.</p>
            <br />
            <p>Signature: _____________________</p>
            <p>Date: ___ / ___ / ______</p>
          </div>

          <div className="text-center mt-12 text-sm text-gx-ink-soft">
            Generated on {new Date().toLocaleDateString()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileMember;
